/**
 * Memory Optimization — strength, forgetting risk, next review time.
 * Deterministic formulas; no LLM.
 */

import type { UserCardProgress } from "./types";
import type { StudyModeKey } from "./types";
import { MEMORY, RECOMMENDED_MODE } from "./constants";
import { difficultyScoreToLevel } from "./difficulty";
import { computeDifficultyScore } from "./difficulty";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysBetween(a: string | null, b: string | null): number {
  if (!a || !b) return 0;
  const d = (new Date(b).getTime() - new Date(a).getTime()) / MS_PER_DAY;
  return Math.max(0, d);
}

/**
 * Memory strength (0–100). Increases with correct reviews and streaks; decreases with time and failures.
 */
export function computeMemoryStrength(progress: UserCardProgress, now: Date): number {
  const seen = progress.timesSeen;
  if (seen === 0) return MEMORY.STRENGTH_MIN;

  const lastCorrect = progress.lastCorrectAt;
  const daysSinceCorrect = lastCorrect ? daysBetween(lastCorrect, now.toISOString()) : 999;
  const correctCount = progress.timesCorrect;
  const incorrectCount = progress.timesIncorrect;
  const streak = progress.currentStreak;

  let strength = 20; // base for having seen the card
  strength += Math.min(40, correctCount * 2);
  strength += Math.min(25, streak * 5);
  strength -= Math.min(30, incorrectCount * MEMORY.PENALTY_PER_INCORRECT / 10);
  strength -= Math.min(40, daysSinceCorrect * MEMORY.DECAY_PER_DAY);

  return Math.round(Math.max(MEMORY.STRENGTH_MIN, Math.min(MEMORY.STRENGTH_MAX, strength)));
}

/**
 * Forgetting risk (0–100). Higher when long time since review, low strength, or recent failure.
 */
export function computeForgettingRisk(progress: UserCardProgress, now: Date): number {
  const strength = progress.memoryStrength ?? computeMemoryStrength(progress, now);
  const lastCorrect = progress.lastCorrectAt;
  const daysSinceCorrect = lastCorrect ? daysBetween(lastCorrect, now.toISOString()) : 999;
  const nextReview = progress.nextReviewAt;
  const daysOverdue = nextReview
    ? Math.max(0, (now.getTime() - new Date(nextReview).getTime()) / MS_PER_DAY)
    : 1;

  let risk = 100 - strength;
  if (daysSinceCorrect > 7) risk += Math.min(30, (daysSinceCorrect - 7) * 3);
  if (daysOverdue > MEMORY.OVERDUE_DAYS_THRESHOLD) {
    risk += Math.min(25, daysOverdue * 5);
  }
  if (progress.lastIncorrectAt && progress.lastCorrectAt) {
    const lastInc = new Date(progress.lastIncorrectAt).getTime();
    const lastCor = new Date(progress.lastCorrectAt).getTime();
    if (lastInc > lastCor) risk += 15; // last answer was wrong
  }

  return Math.round(Math.max(0, Math.min(MEMORY.RISK_MAX, risk)));
}

/**
 * Next review datetime. Based on strength, last correctness, and difficulty.
 */
export function computeNextReviewAt(progress: UserCardProgress, now: Date): string {
  const strength = progress.memoryStrength ?? computeMemoryStrength(progress, now);
  const lastCorrect = progress.lastCorrectAt;
  const correct = progress.timesCorrect;
  const lastWasCorrect = (() => {
    if (!progress.lastCorrectAt || !progress.lastSeenAt) return false;
    return new Date(progress.lastCorrectAt).getTime() >= new Date(progress.lastSeenAt).getTime() - 1000;
  })();

  let intervalDays = MEMORY.BASE_INTERVAL_DAYS;
  if (lastWasCorrect && correct > 0) {
    if (strength < 30) intervalDays = 1;
    else if (strength < 60) intervalDays = Math.min(7, 1 + Math.floor(strength / 15));
    else intervalDays = Math.min(MEMORY.MAX_INTERVAL_DAYS, 7 + Math.floor((strength - 60) / 4));
  }

  const next = new Date(now);
  next.setDate(next.getDate() + intervalDays);
  next.setHours(0, 0, 0, 0);
  return next.toISOString();
}

/**
 * Overdue score: how many days past next_review_at (0 if not overdue).
 */
export function computeOverdueScore(progress: UserCardProgress, now: Date): number {
  const next = progress.nextReviewAt;
  if (!next) return 0;
  const due = new Date(next).getTime();
  const n = now.getTime();
  if (n <= due) return 0;
  return (n - due) / MS_PER_DAY;
}

/**
 * Stability: consistency of performance (0–100). Higher = more stable.
 */
export function computeStabilityScore(progress: UserCardProgress): number {
  const seen = progress.timesSeen;
  if (seen < 2) return 50;
  const ratio = progress.timesCorrect / seen;
  const streakBonus = Math.min(30, progress.bestStreak * 3);
  return Math.round(Math.min(100, ratio * 70 + streakBonus));
}

/**
 * Recommended mode for next review: learn vs cards vs test vs games.
 */
export function getRecommendedMode(progress: UserCardProgress): StudyModeKey {
  const strength = progress.memoryStrength ?? 0;
  const difficultyScore = progress.difficultyScore ?? computeDifficultyScore(progress);
  const level = difficultyScoreToLevel(difficultyScore);

  if (strength < RECOMMENDED_MODE.LEARN_BELOW_STRENGTH || level === "critical" || level === "hard") {
    return RECOMMENDED_MODE.HARD_PREFER_LEARN ? "learn" : "cards";
  }
  if (strength >= RECOMMENDED_MODE.TEST_ABOVE_STRENGTH) return "test";
  return "cards";
}
